/* ════════════════════════════════════════════════════════════
   FABLE 5 — interaction layer (vanilla, dependency-free)
   - Scroll reveal (IntersectionObserver)
   - Stamp word-swap
   - Stat count-up
   - Header: scrolled state + drawer + a11y dropdowns
   - Marquee track duplication
   - Press-line progress fallback (no animation-timeline support)
   - Multi-step lead qualifier → HubSpot Forms API
     (contract identical to nv.js; adds ?goal= deep-link prefill
      so paid-search landing pages can pre-answer step 1)
   Idempotent; re-inits on Shopify section load in the editor.
   ════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.f5-reveal:not([data-f5-revealed])');
    if (!els.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('f5-in'); el.setAttribute('data-f5-revealed', '1'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('f5-in'); io.unobserve(e.target); }
      });
      /* threshold MUST stay 0 — see nv.js: any element taller than ~10x the
         viewport could never satisfy a percentage threshold and would stay
         invisible forever. Fixed 2026-07-29. */
    }, { threshold: 0, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (el) { el.setAttribute('data-f5-revealed', '1'); io.observe(el); });
  }

  /* ---------- Stamp word-swap ---------- */
  function initKinetic(root) {
    var els = (root || document).querySelectorAll('[data-f5-kinetic]:not([data-f5-kin-on])');
    els.forEach(function (el) {
      el.setAttribute('data-f5-kin-on', '1');
      var words;
      try { words = JSON.parse(el.getAttribute('data-f5-kinetic')); } catch (err) { words = []; }
      if (!words || words.length < 2) return;
      el.textContent = words[0];
      if (REDUCED) return;
      /* Reserve widest word so the line doesn't reflow */
      var probe = document.createElement('span');
      probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;';
      probe.className = el.className;
      document.body.appendChild(probe);
      var max = 0;
      words.forEach(function (w) { probe.textContent = w; max = Math.max(max, probe.offsetWidth); });
      probe.remove();
      if (max) { el.style.display = 'inline-block'; el.style.minWidth = max + 'px'; }
      var i = 0;
      setInterval(function () {
        i = (i + 1) % words.length;
        el.classList.add('f5-swap');
        setTimeout(function () { el.textContent = words[i]; }, 170);
        setTimeout(function () { el.classList.remove('f5-swap'); }, 400);
      }, 2400);
    });
  }

  /* ---------- Stat count-up ---------- */
  function initCountup(root) {
    var els = (root || document).querySelectorAll('[data-f5-countup]:not([data-f5-count-on])');
    if (!els.length) return;
    els.forEach(function (el) { el.setAttribute('data-f5-count-on', '1'); });
    if (REDUCED || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        var el = e.target;
        var target = parseFloat(el.getAttribute('data-f5-countup')) || 0;
        var suffix = el.getAttribute('data-f5-suffix') || '';
        var prefix = el.getAttribute('data-f5-prefix') || '';
        var dur = 1100, start = null;
        function step(ts) {
          if (!start) start = ts;
          var p = Math.min((ts - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = prefix + Math.round(target * eased).toLocaleString() + suffix;
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Header ---------- */
  function initHeader() {
    var header = document.querySelector('[data-f5-header]');
    if (!header || header.getAttribute('data-f5-header-on')) return;
    header.setAttribute('data-f5-header-on', '1');

    var onScroll = function () {
      header.classList.toggle('f5-h-scrolled', window.scrollY > 14);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    var toggle = header.querySelector('[data-f5-menu-toggle]');
    var drawer = header.querySelector('[data-f5-drawer]');
    function setDrawer(open) {
      if (!drawer) return;
      header.classList.toggle('f5-drawer-open', open);
      if (toggle) {
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      }
      /* no body overflow lock here: overflow:hidden on <body> makes it a scroll
         container, which un-sticks the sticky header (and the drawer attached
         to it) the moment the menu opens mid-scroll. The drawer uses
         overscroll-behavior:contain instead. */
    }
    if (toggle && drawer) {
      toggle.addEventListener('click', function () { setDrawer(!header.classList.contains('f5-drawer-open')); });
      drawer.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', function () { setDrawer(false); }); });
      var closeBtn = header.querySelector('[data-f5-drawer-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { setDrawer(false); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && header.classList.contains('f5-drawer-open')) {
          setDrawer(false);
          try { toggle.focus(); } catch (err) {}
        }
      });
    }

    header.querySelectorAll('[data-f5-acc-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = btn.nextElementSibling;
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) panel.style.maxHeight = open ? '0px' : (panel.scrollHeight + 'px');
      });
    });

    header.querySelectorAll('[data-f5-dd]').forEach(function (dd) {
      var trigger = dd.querySelector('[data-f5-dd-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var open = dd.classList.toggle('f5-dd-open');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      document.addEventListener('click', function (e) {
        if (!dd.contains(e.target)) { dd.classList.remove('f5-dd-open'); trigger.setAttribute('aria-expanded', 'false'); }
      });
      dd.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && dd.classList.contains('f5-dd-open')) {
          dd.classList.remove('f5-dd-open');
          trigger.setAttribute('aria-expanded', 'false');
          try { trigger.focus(); } catch (err) {}
        }
      });
    });
  }

  /* ---------- Lazy video autoplay: the mp4 is ~10MB — only fetch/play when
     the video scrolls near the viewport (autoplay attr would download it
     eagerly on page load and wreck LCP) ---------- */
  function initLazyPlay(root) {
    var vids = (root || document).querySelectorAll('video[data-f5-lazyplay]:not([data-f5-lp-on])');
    if (!vids.length) return;
    vids.forEach(function (v) { v.setAttribute('data-f5-lp-on', '1'); });
    if (REDUCED) return;
    if (!('IntersectionObserver' in window)) {
      vids.forEach(function (v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        else v.pause();
      });
    }, { rootMargin: '260px 0px' });
    vids.forEach(function (v) { io.observe(v); });
  }

  /* ---------- Marquee: duplicate track content for seamless loop ---------- */
  function initMarquee(root) {
    (root || document).querySelectorAll('.f5-marquee-track:not([data-f5-marq-on])').forEach(function (track) {
      track.setAttribute('data-f5-marq-on', '1');
      track.innerHTML = track.innerHTML + track.innerHTML;
    });
  }

  /* ---------- Press-line progress: scroll-driven with IO fallback ---------- */
  function initPressline(root) {
    var lines = (root || document).querySelectorAll('[data-f5-pressline]:not([data-f5-pl-on])');
    if (!lines.length) return;
    var native = CSS.supports && CSS.supports('animation-timeline: view()');
    lines.forEach(function (line) {
      line.setAttribute('data-f5-pl-on', '1');
      if (native || REDUCED) return; /* CSS handles it, or motion off */
      var bar = line.querySelector('[data-f5-pl-bar]');
      if (!bar) return;
      var onScroll = function () {
        var r = line.getBoundingClientRect();
        var vh = window.innerHeight;
        var p = Math.min(1, Math.max(0, (vh * 0.82 - r.top) / (r.height + vh * 0.4)));
        bar.style.width = (p * 100) + '%';
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    });
  }

  /* ---------- Multi-step lead qualifier ---------- */
  function f5GetCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  function initQualifier(root) {
    var qzs = (root || document).querySelectorAll('[data-f5-qualifier]:not([data-f5-qz-on])');
    qzs.forEach(function (qz) {
      qz.setAttribute('data-f5-qz-on', '1');
      var steps = Array.prototype.slice.call(qz.querySelectorAll('[data-f5-step]'));
      if (!steps.length) return;
      var bar = qz.querySelector('[data-f5-bar]');
      var backBtn = qz.querySelector('[data-f5-back]');
      var counter = qz.querySelector('[data-f5-counter]');
      var answers = {};
      var idx = 0;

      function render(initial) {
        steps.forEach(function (s, i) { s.hidden = (i !== idx); });
        var pct = Math.round((idx / (steps.length - 1)) * 100);
        if (bar) bar.style.width = pct + '%';
        if (counter) counter.textContent = 'Step ' + (idx + 1) + ' of ' + steps.length;
        if (backBtn) backBtn.style.visibility = idx === 0 ? 'hidden' : 'visible';
        var active = steps[idx];
        if (active.getAttribute('data-step-type') === 'form') mountForm(active);
        if (!initial) {
          var h = active.querySelector('.f5-qz-q');
          if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) {} }
          var rect = qz.getBoundingClientRect();
          if (rect.top < 70 || rect.bottom > window.innerHeight) {
            try { qz.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' }); } catch (e) {}
          }
        }
      }

      function next() { if (idx < steps.length - 1) { idx++; render(false); } }
      function back() { if (idx > 0) { idx--; render(false); } }

      function choose(step, opt, silent) {
        var key = step.getAttribute('data-step-key');
        step.querySelectorAll('.f5-qz-opt').forEach(function (o) { o.classList.remove('f5-sel'); o.setAttribute('aria-pressed', 'false'); });
        opt.classList.add('f5-sel'); opt.setAttribute('aria-pressed', 'true');
        answers[key] = { value: opt.getAttribute('data-value') || opt.textContent.trim(), label: opt.getAttribute('data-label') || opt.textContent.trim() };
        if (!silent) setTimeout(next, 260);
      }

      steps.forEach(function (step) {
        step.querySelectorAll('.f5-qz-opt').forEach(function (opt) {
          opt.addEventListener('click', function () { choose(step, opt, false); });
        });
        var cont = step.querySelector('[data-f5-continue]');
        if (cont) cont.addEventListener('click', next);
      });
      if (backBtn) backBtn.addEventListener('click', back);

      /* ?goal= deep link (paid-search landing pages pre-answer step 1).
         Marks the answer + advances so the visitor lands on step 2. */
      try {
        var goal = new URLSearchParams(window.location.search).get('goal');
        if (goal) {
          var s0 = steps[0];
          if (s0 && s0.getAttribute('data-step-key') === 'goal') {
            var match = s0.querySelector('.f5-qz-opt[data-value="' + goal.replace(/[^a-z-]/gi, '').toLowerCase() + '"]');
            if (match) { choose(s0, match, true); idx = Math.min(1, steps.length - 1); }
          }
        }
      } catch (e) {}

      function mountForm(step) {
        var sum = step.querySelector('[data-f5-summary]');
        if (!sum) return;
        var parts = [];
        ['goal', 'org', 'size'].forEach(function (k) { if (answers[k] && answers[k].label) parts.push(answers[k].label); });
        sum.textContent = parts.length ? parts.join('  ·  ') : '';
        sum.hidden = parts.length === 0;
      }

      /* Custom contact form → HubSpot Forms Submission API (contract preserved) */
      var leadForm = qz.querySelector('[data-f5-qz-form]');
      if (leadForm && !leadForm.getAttribute('data-f5-form-on')) {
        leadForm.setAttribute('data-f5-form-on', '1');
        leadForm.addEventListener('submit', function (e) {
          e.preventDefault();
          if (!leadForm.checkValidity()) { leadForm.reportValidity(); return; }
          var btn = leadForm.querySelector('[data-f5-qz-submit]');
          var errEl = leadForm.querySelector('[data-f5-qz-err]');
          if (errEl) errEl.hidden = true;
          var btnHtml = btn ? btn.innerHTML : '';
          if (btn) { btn.disabled = true; btn.innerHTML = 'Sending…'; }

          var data = new FormData(leadForm);
          var noteLines = [];
          var labels = { goal: 'Making', org: 'For', size: 'Group size' };
          ['goal', 'org', 'size'].forEach(function (k) { if (answers[k]) noteLines.push(labels[k] + ': ' + answers[k].label); });
          var note = 'Lead qualifier — ' + noteLines.join(' · ');
          var userNote = (data.get('notes') || '').toString().trim();
          if (userNote) note += '\n\nThey added: ' + userNote;

          var fields = [
            { name: 'firstname', value: (data.get('firstname') || '').toString().trim() },
            { name: 'lastname', value: (data.get('lastname') || '').toString().trim() },
            { name: 'email', value: (data.get('email') || '').toString().trim() },
            { name: 'company', value: (data.get('company') || '').toString().trim() },
            { name: 'notes_questions', value: note }
          ];
          /* qualifier answers → dedicated contact properties (hidden fields on the HubSpot form) */
          [['goal', 'pyc_goal'], ['org', 'pyc_org_type'], ['size', 'pyc_group_size']].forEach(function (p) {
            if (answers[p[0]] && answers[p[0]].label) fields.push({ name: p[1], value: answers[p[0]].label });
          });
          var portal = qz.getAttribute('data-hs-portal');
          var formId = qz.getAttribute('data-hs-form');
          var redirect = qz.getAttribute('data-redirect') || '/pages/form-submitted';
          /* the thank-you page gates generate_lead on ?ref=form — guarantee it */
          if (redirect.indexOf('ref=') === -1) redirect += (redirect.indexOf('?') === -1 ? '?' : '&') + 'ref=form';

          var attributionReady = Promise.resolve();
          if (window.PYCMarketingAttribution) {
            var preparedLead = window.PYCMarketingAttribution.prepareLead({
              email: data.get('email'),
              firstName: data.get('firstname'),
              lastName: data.get('lastname')
            });
            attributionReady = preparedLead.ready;
            redirect = window.PYCMarketingAttribution.leadRedirect(redirect, preparedLead.eventId);
          }

          try {
            /* gtag (not a bare dataLayer.push) — without GTM only gtag-style pushes reach GA4 */
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'lead_qualifier_submit', {
                pyc_goal: (answers.goal || {}).value || '',
                pyc_org: (answers.org || {}).value || '',
                pyc_size: (answers.size || {}).value || ''
              });
            } else {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: 'lead_qualifier_submit', pyc_goal: (answers.goal || {}).value, pyc_org: (answers.org || {}).value, pyc_size: (answers.size || {}).value });
            }
          } catch (e2) {}

          var attributedPageUri = window.PYCMarketingAttribution
            ? new URL(window.PYCMarketingAttribution.decorateUrl(window.location.href), window.location.origin).toString()
            : window.location.href;
          var payload = { fields: fields, context: { pageUri: attributedPageUri, pageName: document.title } };
          var hutk = f5GetCookie('hubspotutk');
          if (hutk) payload.context.hutk = hutk;

          var done = false;
          function fail(msg) {
            if (done) return; done = true;
            if (btn) { btn.disabled = false; btn.innerHTML = btnHtml; }
            if (errEl) { errEl.hidden = false; errEl.textContent = msg || 'Something went wrong. Please try again, or email info@printyourcause.com.'; }
          }
          if (!portal || !formId) { fail(); return; }

          var hsHost = (qz.getAttribute('data-hs-region') === 'eu1') ? 'api-eu1.hsforms.com' : 'api.hsforms.com';
          fetch('https://' + hsHost + '/submissions/v3/integration/submit/' + portal + '/' + formId, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
          }).then(function (r) {
            if (r.ok) {
              done = true;
              return attributionReady.catch(function () {}).then(function () { window.location.href = redirect; });
            }
            return r.json().then(function (j) {
              var m = j && (j.message || (j.errors && j.errors[0] && j.errors[0].message));
              fail(m && /email/i.test(m) ? 'Please enter a valid email address.' : null);
            }).catch(function () { fail(); });
          }).catch(function () { fail("Couldn't reach the server — please check your connection and try again."); });
        });
      }

      render(true);
    });
  }

  /* ---------- Font gate: start accent settle only once webfonts land ---------- */
  (function () {
    var arm = function () { document.documentElement.classList.add('f5-fonts-in'); };
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(arm);
      setTimeout(arm, 2500); /* safety: never wait forever */
    } else {
      arm();
    }
  })();

  /* ---------- Boot ---------- */
  function boot(root) {
    if (REDUCED) {
      (root || document).querySelectorAll('video[autoplay]').forEach(function (v) {
        v.removeAttribute('autoplay');
        try { v.pause(); } catch (e) {}
      });
    }
    initReveal(root);
    initKinetic(root);
    initCountup(root);
    initMarquee(root);
    initPressline(root);
    initLazyPlay(root);
    initQualifier(root);
    initHeader();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:reorder', function () { boot(document); });
})();
